import { Checkbox, Switch, Table, Flex, Tooltip } from "antd";

import TaskIcon from "@/icons/TaskIcon";
import CubIcon from "@/icons/CubIcon";
import StarIcon from "@/icons/StarIcon";
import FolderOpenIcon from "@/icons/FolderOpenIcon";
import AdminIcon from "@/icons/AdminIcon";
import LockCloseIcon from "@/icons/LockClosedIcon";
import { useMemo, useCallback } from "react";

const COLUMN_LABELS = {
  action: "",
  notification_type: "Раздел",
  rule_name: "Событие",
  email: "Почта",
  in_app: "Лента",
  push: "Пуши",
  // rule_description: "Адресаты",
};

const NoticesSettingsTable = ({
  data,
  loading,
  onChange,
  isSettings = false,
  scroll = { x: "100%" },
}) => {
  const ICON_MAP = useMemo(
    () => ({
      task: <TaskIcon />,
      object: <CubIcon />,
      group: <StarIcon />,
      file: <FolderOpenIcon />,
      system: <AdminIcon />,
    }),
    []
  );

  const getIcon = useCallback((type) => ICON_MAP[type] || null, [ICON_MAP]);

  const isChannelDisabled = useCallback(
    (item) =>
      !isSettings &&
      (!item.can_user_disable || !item.is_enabled || item.parentIsEnabled),
    [isSettings]
  );

  const handleChangeChannels = useCallback(
    (record, channel, checked) => {
      const { parentIsEnabled, ...cleanRecord } = record || {};
      const channels = Array.isArray(record.channels) ? record.channels : [];
      const next = checked
        ? [...channels, channel]
        : channels.filter((c) => c !== channel);
      onChange({ ...cleanRecord, channels: next });
    },
    [onChange]
  );
  // Подготавливаем дерево данных: если родитель включен (is_enabled === true),
  // то у дочерних элементов помечаем parentIsEnabled = true, чтобы задизейблить их переключатели.
  const processedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const mapChildren = (items) =>
      items.map((item) => {
        const next = { ...item };
        if (Array.isArray(item.children) && item.children.length) {
          next.children = item.children.map((child) => ({
            ...child,
            parentIsEnabled: !!item.is_enabled,
          }));
          // Рекурсивно обрабатываем вложенные уровни
          next.children = mapChildren(next.children);
        }
        return next;
      });
    return mapChildren(data);
  }, [data]);

  const columns = useMemo(() => {
    const CHANNELS = [
      { key: "email", title: COLUMN_LABELS.email },
      { key: "in_app", title: COLUMN_LABELS.in_app },
      { key: "push", title: COLUMN_LABELS.push },
    ];

    return [
      {
        title: COLUMN_LABELS.action,
        dataIndex: "action",
        key: "action",
        width: 45,
      },
      !isSettings
        ? {
            title: (
              <div style={{ paddingLeft: 12 }}>
                <Checkbox indeterminate disabled />
              </div>
            ),
            dataIndex: "check",
            key: "check",
            width: 70,
            render: (_, record) => (
              <Switch
                disabled={!record.can_user_disable || record.parentIsEnabled}
                checked={!record.parentIsEnabled && !!record.is_enabled}
                onChange={(checked) => {
                  const { parentIsEnabled, ...cleanRecord } = record || {};
                  onChange({ ...cleanRecord, is_enabled: checked });
                }}
              />
            ),
          }
        : null,
      {
        title: COLUMN_LABELS.notification_type,
        dataIndex: "notification_type",
        key: "notification_type",
        width: 130,
        ellipsis: { showTitle: true },
        sorter: {
          compare: (a, b) =>
            (a.notification_type?.name || "").localeCompare(
              b.notification_type?.name || ""
            ),
        },
        render: (_, item) =>
          item.notification_type && (
            <Flex
              align="center"
              gap={3}
              style={{
                display: "inline-flex",
                border: "1px solid var(--color-gray)",
                padding: "0 6px",
                borderRadius: "4px",
                fontSize: "12px",
                lineHeight: "18px",
                color: "var(--color-deep-blue)",
              }}
            >
              {getIcon(item.notification_type?.icon)}
              {item.notification_type?.name}
            </Flex>
          ),
      },
      {
        title: COLUMN_LABELS.rule_name,
        dataIndex: "rule_name",
        key: "rule_name",
        width: 200,
        ellipsis: true,
        sorter: {
          compare: (a, b) =>
            (a.rule_name || "").localeCompare(b.rule_name || ""),
        },
        render: (rule_name) => (
          <Tooltip placement="bottomLeft" title={rule_name}>
            {rule_name}
          </Tooltip>
        ),
      },
      ...CHANNELS.map(({ key, title }) => ({
        title,
        dataIndex: key,
        key,
        align: "center",
        width: 65,
        render: (_, item) => (
          <Checkbox
            disabled={isChannelDisabled(item)}
            checked={
              Array.isArray(item.channels) && item.channels.includes(key)
            }
            onChange={(e) => handleChangeChannels(item, key, e.target.checked)}
          />
        ),
      })),
      isSettings
        ? {
            title: (
              <div>
                <LockCloseIcon style={{ fontSize: 14 }} />
              </div>
            ),
            dataIndex: "check",
            key: "check",
            width: 70,
            align: "center",
            render: (_, record) => (
              <Switch
                checked={!record.can_user_disable}
                onChange={(checked) => {
                  const { parentIsEnabled, ...cleanRecord } = record || {};
                  onChange({ ...cleanRecord, can_user_disable: !checked });
                }}
              />
            ),
          }
        : null,
      // {
      //   title: COLUMN_LABELS.rule_description,
      //   dataIndex: "rule_description",
      //   key: "rule_description",
      //   width: 300,
      //   ellipsis: true,
      //   sorter: {
      //     compare: (a, b) =>
      //       (a.rule_description || "").localeCompare(b.rule_description || ""),
      //   },
      //   render: (rule_description) => (
      //     <Tooltip placement="bottomLeft" title={rule_description}>
      //       {rule_description}
      //     </Tooltip>
      //   ),
      // },
    ].filter(Boolean);
  }, [getIcon, handleChangeChannels, isChannelDisabled, isSettings, onChange]);

  return (
    <Table
      dataSource={processedData}
      columns={columns}
      rowKey="rule_uuid"
      pagination={false}
      loading={loading}
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      scroll={scroll}
      tableLayout="fixed"
      bordered
      rowClassName={(record) =>
        !isSettings && record.parentIsEnabled
          ? "top-align disabled-row"
          : "top-align"
      }
      expandable={{
        expandedRowClassName: "expanded-row",
      }}
    />
  );
};

export default NoticesSettingsTable;
